import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Grid,
  CircularProgress,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useEvents } from '../../contexts/EventContext';
import { formatDate } from '../../utils/helpers';
import { getCategoryLabel, getCategoryColor } from '../../types';
import api from '../../services/api';
import { orderRegistrationsByEvents } from '../../utils/registrationOrder';
import { csvEsc, attendeeName, photoPermLabel, dietaryLabel } from '../../utils/attendeeExport';

interface DetailAttendee {
  id: string;
  checkedIn: boolean;
  familyMember?: {
    name: string;
    relationship?: string;
    photoPermission?: boolean;
    dietaryRequirements?: string | null;
  } | null;
  eventGuest?: {
    firstName: string;
    lastName: string;
    relationship?: string;
    dietaryRequirements?: string | null;
  } | null;
}

interface EventRegistration {
  id: string;
  eventId: string;
  userId: string;
  attendeeCount: number;
  specialRequirements: string | null;
  status: string;
  registeredAt: string;
  attendees?: DetailAttendee[];
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

type FilterType = 'all' | 'past' | 'upcoming';

const AttendanceReportContent: React.FC = () => {
  const { events } = useEvents();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [allRegistrations, setAllRegistrations] = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch all registrations for all events in ONE request. This used to call
  // /registrations/event/:id once per event, one at a time: 250 requests plus 250 CORS
  // preflights, 20.7 s on prod (2026-09-26), almost all of it round trips.
  const fetchAllRegistrations = useCallback(async () => {
    if (events.length === 0) return;
    setLoading(true);
    try {
      const res = await api.get<EventRegistration[]>('/registrations/all');
      setAllRegistrations(orderRegistrationsByEvents(res.data, events));
    } catch {
      setAllRegistrations([]);
    } finally {
      setLoading(false);
    }
  }, [events]);

  useEffect(() => {
    fetchAllRegistrations();
  }, [fetchAllRegistrations]);

  // Build a map of userId -> user details from registrations
  const userMap = new Map<string, { firstName: string; lastName: string; email: string }>();
  allRegistrations.forEach((reg) => {
    if (reg.user && !userMap.has(reg.userId)) {
      userMap.set(reg.userId, {
        firstName: reg.user.firstName,
        lastName: reg.user.lastName,
        email: reg.user.email,
      });
    }
  });

  // Get unique users from registrations
  const uniqueUserIds = Array.from(userMap.keys());

  // Filter events based on filter type
  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.date);
    const today = new Date();

    if (filterType === 'past') {
      return eventDate < today;
    } else if (filterType === 'upcoming') {
      return eventDate >= today;
    }
    return true;
  });

  // Get user details
  const getUserDetails = (userId: string) => {
    return userMap.get(userId) || { firstName: 'Unknown', lastName: 'User', email: 'N/A' };
  };

  // Check if user attended an event
  const getUserEventRegistration = (userId: string, eventId: string) => {
    return allRegistrations.find(
      (r) => r.userId === userId && r.eventId === eventId && r.status === 'confirmed'
    );
  };

  // Filter users based on search
  const filteredUsers = uniqueUserIds.filter((userId) => {
    const user = getUserDetails(userId);
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  // Calculate user statistics
  const getUserStats = (userId: string) => {
    const userRegistrations = allRegistrations.filter(
      (r) => r.userId === userId && r.status === 'confirmed'
    );
    const totalEvents = userRegistrations.length;
    const totalAttendees = userRegistrations.reduce((sum, r) => sum + r.attendeeCount, 0);
    return { totalEvents, totalAttendees };
  };

  // Export report to CSV
  const handleExportCSV = () => {
    const headers = [
      'Family Name',
      'Email',
      'Total Events',
      'Total Attendees',
      ...filteredEvents.map((e) => e.title),
    ];

    const rows = filteredUsers.map((userId) => {
      const user = getUserDetails(userId);
      const stats = getUserStats(userId);
      const eventAttendance = filteredEvents.map((event) => {
        const reg = getUserEventRegistration(userId, event.id);
        return reg ? reg.attendeeCount.toString() : '-';
      });

      return [
        `${user.firstName} ${user.lastName}`,
        user.email,
        stats.totalEvents.toString(),
        stats.totalAttendees.toString(),
        ...eventAttendance,
      ];
    });

    const csvContent = [
      'Attendance Report',
      `Generated: ${new Date().toLocaleDateString()}`,
      `Filter: ${filterType === 'all' ? 'All Events' : filterType === 'past' ? 'Past Events' : 'Upcoming Events'}`,
      '',
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Attendance_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // C7 — a per-attendee detail export (one row per person) with Photo
  // Permission + Dietary. Separate from the cross-tab matrix above, which can't
  // carry per-attendee columns.
  const eventById = new Map(events.map((e) => [e.id, e]));
  const handleExportAttendeeDetailCSV = () => {
    const headers = ['Event', 'Attendee Name', 'Relationship', 'Photo Permission', 'Dietary', 'Family (account)', 'Email', 'Status'];
    const rows = allRegistrations.flatMap((reg) => {
      const event = eventById.get(reg.eventId);
      const family = `${reg.user?.firstName || ''} ${reg.user?.lastName || ''}`.trim();
      const email = reg.user?.email || '';
      const list = reg.attendees ?? [];
      if (list.length === 0) {
        return [[
          csvEsc(event?.title || 'Unknown'), csvEsc(`${reg.attendeeCount} attendee(s)`), csvEsc(''),
          csvEsc('N/A'), csvEsc(''), csvEsc(family), csvEsc(email), csvEsc(reg.status),
        ].join(',')];
      }
      return list.map((a) => [
        csvEsc(event?.title || 'Unknown'),
        csvEsc(attendeeName(a)),
        csvEsc(a.familyMember?.relationship || a.eventGuest?.relationship || ''),
        csvEsc(photoPermLabel(a)),
        csvEsc(dietaryLabel(a)),
        csvEsc(family),
        csvEsc(email),
        csvEsc(reg.status),
      ].join(','));
    });

    const csvContent = [
      'Attendee Detail Report',
      `Generated: ${new Date().toLocaleDateString()}`,
      '',
      headers.map(csvEsc).join(','),
      ...rows,
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Attendee_Detail_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Calculate event totals
  const getEventTotals = (eventId: string) => {
    const eventRegs = allRegistrations.filter(
      (r) => r.eventId === eventId && r.status === 'confirmed'
    );
    return eventRegs.reduce((sum, r) => sum + r.attendeeCount, 0);
  };

  return (
    <Box>
      {/* Filters and Actions */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search families..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter Events</InputLabel>
                <Select
                  value={filterType}
                  label="Filter Events"
                  onChange={(e) => setFilterType(e.target.value as FilterType)}
                >
                  <MenuItem value="all">All Events</MenuItem>
                  <MenuItem value="past">Past Events Only</MenuItem>
                  <MenuItem value="upcoming">Upcoming Events Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent={{ sm: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportCSV}
                  disabled={filteredUsers.length === 0}
                >
                  Export Report
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportAttendeeDetailCSV}
                  disabled={allRegistrations.length === 0}
                >
                  Export attendee detail (CSV)
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <PersonIcon sx={{ fontSize: 32, color: '#00b7ed', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {uniqueUserIds.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Families
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <EventIcon sx={{ fontSize: 32, color: '#4caf50', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {filteredEvents.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 32, color: '#ff9800', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {allRegistrations.filter((r) => r.status === 'confirmed').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Registrations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <AssessmentIcon sx={{ fontSize: 32, color: '#9c27b0', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {allRegistrations
                  .filter((r) => r.status === 'confirmed')
                  .reduce((sum, r) => sum + r.attendeeCount, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Attendees
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Attendance Matrix */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <AssessmentIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No attendance data available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm
              ? 'No families match your search criteria.'
              : 'Attendance records will appear here once families register for events.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: '#f5f5f5',
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    minWidth: 200,
                  }}
                >
                  <strong>Family</strong>
                </TableCell>
                <TableCell sx={{ backgroundColor: '#f5f5f5', minWidth: 80 }} align="center">
                  <strong>Events</strong>
                </TableCell>
                {filteredEvents.map((event) => (
                  <TableCell
                    key={event.id}
                    sx={{
                      backgroundColor: '#f5f5f5',
                      minWidth: 120,
                      maxWidth: 150,
                    }}
                    align="center"
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Chip
                        label={getCategoryLabel(event.category)}
                        size="small"
                        sx={{
                          backgroundColor: getCategoryColor(event.category),
                          color: 'white',
                          fontSize: '0.6rem',
                          height: 16,
                          mb: 0.5,
                        }}
                      />
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 140,
                        }}
                        title={event.title}
                      >
                        {event.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(event.date)}
                      </Typography>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((userId) => {
                const user = getUserDetails(userId);
                const stats = getUserStats(userId);

                return (
                  <TableRow key={userId} hover>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'white',
                        zIndex: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={stats.totalEvents}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    {filteredEvents.map((event) => {
                      const registration = getUserEventRegistration(userId, event.id);
                      return (
                        <TableCell key={event.id} align="center">
                          {registration ? (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                              label={registration.attendeeCount}
                              size="small"
                              color="success"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {/* Totals Row */}
              <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#e3f2fd',
                    zIndex: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    TOTAL ATTENDEES
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={700}>
                    -
                  </Typography>
                </TableCell>
                {filteredEvents.map((event) => (
                  <TableCell key={event.id} align="center">
                    <Typography variant="body2" fontWeight={700} color="primary">
                      {getEventTotals(event.id)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AttendanceReportContent;
